(() => {
  if (window.baTokyoCategoryNavigation) return;
  window.baTokyoCategoryNavigation = true;

  if (!customElements.get('ba-tokyo-category-picker')) {
    customElements.define('ba-tokyo-category-picker', class extends HTMLElement {
      connectedCallback() {
        if (this.media) return;
        this.button = this.querySelector('.ba-tokyo-category-toggle');
        this.options = this.querySelector('nav');
        if (!this.button || !this.options) return;
        this.expanded = false;
        this.media = window.matchMedia('(max-width: 749px)');
        this.onToggle = () => {
          if (!this.media.matches) return;
          this.expanded = !this.expanded;
          this.render();
        };
        this.onEscape = event => {
          if (event.key !== 'Escape' || !this.media.matches || !this.expanded) return;
          event.preventDefault();
          event.stopPropagation();
          this.expanded = false;
          this.render();
          this.button.focus();
        };
        this.onViewport = () => {
          this.expanded = false;
          this.render();
        };
        this.button.addEventListener('click', this.onToggle);
        this.addEventListener('keydown', this.onEscape);
        this.media.addEventListener('change', this.onViewport);
        this.setAttribute('data-ba-enhanced', '');
        this.render();
      }

      disconnectedCallback() {
        if (!this.media) return;
        this.button.removeEventListener('click', this.onToggle);
        this.removeEventListener('keydown', this.onEscape);
        this.media.removeEventListener('change', this.onViewport);
        this.removeAttribute('data-ba-enhanced');
        this.button.hidden = true;
        this.options.hidden = false;
        this.media = null;
      }

      render() {
        const open = !this.media.matches || this.expanded;
        const restoreFocus = !open && this.options.contains(document.activeElement);
        this.button.hidden = !this.media.matches;
        this.options.hidden = !open;
        this.button.setAttribute('aria-expanded', String(open));
        if (restoreFocus) this.button.focus({ preventScroll: true });
      }
    });
  }

  const selector = '[data-ba-mode-view][data-ba-section-id]';
  const getCatalog = () => document.querySelector(selector);
  const getStatus = () => document.querySelector('[data-ba-category-status]');
  const FacetForm = customElements.get('facet-filters-form');
  const facetControllers = new Set();
  let categoryController;
  let categoryRequest = 0;
  let facetRequest = 0;
  let renderedPath = window.location.pathname;

  const abortFacets = () => {
    facetRequest += 1;
    facetControllers.forEach(controller => controller.abort());
    facetControllers.clear();
  };

  const setBusy = (catalog, busy) => {
    if (!catalog) return;
    if (busy) catalog.setAttribute('aria-busy', 'true');
    else catalog.removeAttribute('aria-busy');
    const facets = catalog.querySelector('facet-filters-form');
    if (facets) facets.inert = busy;
    if (!busy) catalog.querySelectorAll('[data-ba-pending]').forEach(link => link.removeAttribute('data-ba-pending'));
  };

  const matchesMode = (url, catalog) => {
    const path = catalog.dataset.baModePath;
    return url.origin === window.location.origin
      && (url.pathname === path || url.pathname.startsWith(`${path}-`))
      && url.searchParams.get('view') === catalog.dataset.baModeView;
  };

  const updatePageDetails = (catalog, url) => {
    document.title = catalog.dataset.baPageTitle;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = catalog.dataset.baCanonicalUrl;
    const breadcrumb = document.querySelector('#MainContent .breadcrumb .breadcrumb-item a');
    if (breadcrumb) {
      breadcrumb.textContent = catalog.dataset.baCollectionTitle;
      breadcrumb.href = `${url.pathname}${url.search}`;
    }
  };

  const navigate = async (url, { push = true, focus = false, pendingLink = null } = {}) => {
    const catalog = getCatalog();
    if (!catalog || !matchesMode(url, catalog)) return;
    categoryController?.abort();
    abortFacets();
    const request = ++categoryRequest;
    const controller = new AbortController();
    categoryController = controller;
    setBusy(catalog, false);
    setBusy(catalog, true);
    pendingLink?.setAttribute('data-ba-pending', '');
    const status = getStatus();
    if (status) status.textContent = catalog.dataset.baLoadingLabel;
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('section_id', catalog.dataset.baSectionId);
    const navigationTop = catalog.querySelector('.ba-tokyo-catalog-navigation')?.getBoundingClientRect().top;
    const previousScroll = window.scrollY;

    try {
      const response = await fetch(requestUrl, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Category unavailable');
      const html = await response.text();
      if (request !== categoryRequest) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector(selector);
      if (!next || next.dataset.baModeView !== catalog.dataset.baModeView) throw new Error('Category markup unavailable');
      if (document.body.classList.contains('collection-layout-sidebar-drawer-open') && typeof handleSidebarDismiss === 'function') handleSidebarDismiss();
      const replacement = document.importNode(next, true);
      catalog.replaceWith(replacement);
      if (push) history.pushState({ searchParams: url.searchParams.toString(), baCategory: true }, '', `${url.pathname}${url.search}${url.hash}`);
      renderedPath = url.pathname;
      if (FacetForm) {
        FacetForm.searchParamsPrev = url.searchParams.toString();
        FacetForm.filterData = [];
      }
      updatePageDetails(replacement, url);
      if (previousScroll > 0 && Number.isFinite(navigationTop)) {
        const nextTop = replacement.querySelector('.ba-tokyo-catalog-navigation')?.getBoundingClientRect().top;
        if (Number.isFinite(nextTop)) window.scrollBy({ top: nextTop - navigationTop, behavior: 'instant' });
      }
      if (focus) {
        const picker = replacement.querySelector('ba-tokyo-category-picker');
        const focusTarget = picker?.querySelector('nav')?.hidden
          ? picker.querySelector('.ba-tokyo-category-toggle')
          : replacement.querySelector('[aria-current="page"]');
        focusTarget?.focus({ preventScroll: true });
      }
      if (status) status.textContent = replacement.dataset.baAnnouncement;
    } catch (error) {
      if (error.name !== 'AbortError' && request === categoryRequest) window.location.assign(url.href);
    } finally {
      if (request === categoryRequest) {
        categoryController = null;
        setBusy(getCatalog(), false);
      }
    }
  };

  if (FacetForm) {
    const nativeRenderPage = FacetForm.renderPage;
    const nativeFetch = FacetForm.renderSectionFromFetch;
    FacetForm.renderPage = function (searchParams, event, ...args) {
      if (getCatalog()) {
        if (categoryController) return;
        if (event?.target && !event.target.isConnected) return;
        abortFacets();
      }
      return nativeRenderPage.call(this, searchParams, event, ...args);
    };
    FacetForm.renderSectionFromFetch = function (url, event, appendResults) {
      const catalog = getCatalog();
      if (!catalog) return nativeFetch.call(this, url, event, appendResults);
      const request = facetRequest;
      const controller = new AbortController();
      facetControllers.add(controller);
      return fetch(url, { signal: controller.signal, credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Filters unavailable');
          return response.text();
        })
        .then(html => {
          if (request !== facetRequest || catalog !== getCatalog()) return;
          this.filterData = [...this.filterData.slice(-19), { html, url }];
          this.renderCollectionActions(html);
          this.renderFilters(html, event);
          this.renderProductGridContainer(html, appendResults);
          this.renderProductCount(html);
        })
        .catch(error => {
          if (error.name !== 'AbortError' && request === facetRequest && catalog === getCatalog()) {
            const fallback = new URL(url, window.location.href);
            fallback.searchParams.delete('section_id');
            window.location.assign(fallback.href);
          }
        })
        .finally(() => facetControllers.delete(controller));
    };
  }

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('.ba-tokyo-category-link, [data-ba-category-link]');
    const catalog = link?.closest(selector);
    if (!catalog || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href);
    if (!matchesMode(url, catalog)) return;
    event.preventDefault();
    if (url.href === window.location.href && !categoryController) return;
    navigate(url, { focus: catalog.contains(document.activeElement), pendingLink: link });
  });

  window.addEventListener('popstate', event => {
    const catalog = getCatalog();
    if (!catalog) return;
    const url = new URL(window.location.href);
    if (!matchesMode(url, catalog)) return;
    if (categoryController) {
      categoryController.abort();
      categoryRequest += 1;
      categoryController = null;
      setBusy(catalog, false);
      const status = getStatus();
      if (status) status.textContent = catalog.dataset.baAnnouncement;
    }
    if (url.pathname !== renderedPath) {
      event.stopImmediatePropagation();
      navigate(url, { push: false });
    }
  }, true);
})();

